import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle, BookOpen, FileText, Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PublishersPage = () => {
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchPublishers();
  }, []);

  const fetchPublishers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/publishers?verified_only=false&limit=100`);
      if (res.ok) {
        const data = await res.json();
        setPublishers(data.publishers || []);
      }
    } catch (err) {
      console.error('Error fetching publishers:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredPublishers = publishers.filter(p =>
    p.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gray-950 px-4 py-8" data-testid="publishers-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Publishers</h1>
          <p className="text-gray-400">Browse comics and novels by publisher</p>
        </div>

        {/* Search */}
        <div className="relative mb-8">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search publishers..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full md:w-80 pl-10 pr-4 py-2 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
            data-testid="search-publishers"
          />
        </div>

        {/* Publishers Grid */}
        {loading ? (
          <div className="flex justify-center py-12" data-testid="publishers-loading">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : filteredPublishers.length === 0 ? (
          <div className="text-center py-12 text-gray-500" data-testid="publishers-empty">
            {search ? (
              <p>No publishers found matching "{search}"</p>
            ) : (
              <p>No publishers yet</p>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="publishers-grid">
            {filteredPublishers.map((publisher) => (
              <Link
                key={publisher.id}
                to={`/publisher/${publisher.slug}`}
                className="group p-4 bg-gray-900 border border-gray-800 rounded-xl hover:border-purple-500/50 transition-colors"
                data-testid={`publisher-card-${publisher.id}`}
              >
                <div className="flex items-start gap-4">
                  {/* Logo */}
                  <div className="w-16 h-16 bg-gray-800 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden">
                    {publisher.logo_url ? (
                      <img
                        src={publisher.logo_url}
                        alt={publisher.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <span className="text-2xl font-bold text-gray-600">
                        {publisher.name.charAt(0).toUpperCase()}
                      </span>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-semibold text-white group-hover:text-purple-400 transition-colors truncate">
                        {publisher.name}
                      </h3>
                      {publisher.verified && (
                        <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                      )}
                    </div>

                    {publisher.description && (
                      <p className="text-sm text-gray-500 line-clamp-2 mb-2">
                        {publisher.description}
                      </p>
                    )}

                    {/* Stats */}
                    <div className="flex items-center gap-4 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3 h-3" />
                        {publisher.series_count || 0} Comics
                      </span>
                      <span className="flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {publisher.novel_count || 0} Novels
                      </span>
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PublishersPage;
